import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import "@/App.css";

import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import GetStarted from "@/pages/GetStarted";
import AuthCallback from "@/pages/AuthCallback";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminStores from "@/pages/admin/AdminStores";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPlatformSettings from "@/pages/admin/AdminPlatformSettings";
import AdminAudit from "@/pages/admin/AdminAudit";
import StoreLayout from "@/pages/store/StoreLayout";
import StoreDashboard from "@/pages/store/StoreDashboard";
import StoreJobs from "@/pages/store/StoreJobs";
import StorePricing from "@/pages/store/StorePricing";
import StorePrinter from "@/pages/store/StorePrinter";
import StorePublicUrl from "@/pages/store/StorePublicUrl";
import StoreSubscription from "@/pages/store/StoreSubscription";
import StoreSettings from "@/pages/store/StoreSettings";
import StoreCoupons from "@/pages/store/StoreCoupons";
import StoreNotifications from "@/pages/store/StoreNotifications";
import PublicStore from "@/pages/customer/PublicStore";
import TrackOrder from "@/pages/customer/TrackOrder";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (user.must_change_password && loc.pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Landing />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === "super_admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/store" replace />;
}

function AppRouter() {
  const loc = useLocation();
  // CRITICAL: detect Google Auth session_id fragment BEFORE any protected route runs.
  // Must read from useLocation().hash (reactive), not window.location.hash.
  if (loc.hash && loc.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RoleHome />} />
      <Route path="/get-started" element={<GetStarted />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />

      <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="stores" element={<AdminStores />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="platform-settings" element={<AdminPlatformSettings />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      <Route path="/store" element={<Protected roles={["store_owner", "staff"]}><StoreLayout /></Protected>}>
        <Route index element={<StoreDashboard />} />
        <Route path="jobs" element={<StoreJobs />} />
        <Route path="notifications" element={<StoreNotifications />} />
        <Route path="coupons" element={<StoreCoupons />} />
        <Route path="pricing" element={<StorePricing />} />
        <Route path="printer" element={<StorePrinter />} />
        <Route path="public-url" element={<StorePublicUrl />} />
        <Route path="subscription" element={<StoreSubscription />} />
        <Route path="settings" element={<StoreSettings />} />
      </Route>

      <Route path="/s/:slug" element={<PublicStore />} />
      <Route path="/track" element={<TrackOrder />} />
      <Route path="/track/:jobNumber" element={<TrackOrder />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
